import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, MapPin, Building2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { businessApi, errorMessage } from '../../lib/api';

import { trackBeginCheckout } from '../../utils/analytics';

import { SELECTED_TIER_KEY } from '../Pricing';

// Phase 1 (ADR-0009 §2) ships two plans: Local and Multi. Colour and icon follow the
// tier's own `color_theme`; the highlight is driven by `badge_text` alone.
const THEME_COLORS = {
  orange: '#f97316',
  purple: '#8b5cf6',
  teal: '#14b8a6',
  indigo: '#6366f1',
  green: '#22c55e',
  gray: '#6b7280',
};
const TIER_COLORS = { local: '#f97316', multi: '#8b5cf6' };
const TIER_ICONS = { local: MapPin, multi: Building2 };

// Same money formatting as the public pricing page (Pricing.jsx): thousands separator,
// cents only when the number actually has them.
const fmt = (n) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: (Number(n || 0) % 1) ? 2 : 0 });

const SubscriptionPlans = () => {
  const [searchParams] = useSearchParams();
  const preselected = (searchParams.get('plan') || localStorage.getItem(SELECTED_TIER_KEY) || '').toLowerCase();
  const canceled = searchParams.get('canceled') === 'true';
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [pendingPlanTier, setPendingPlanTier] = useState(null);
  const { isAuthenticated, openAuthModal } = useAuth();
  const hasTriggeredCheckout = useRef(false);

  const initiateCheckout = useCallback(async (tier) => {
    setCheckoutLoading(tier);

    try {
      const response = await businessApi.post('/stripe/create-checkout-session', {
        plan_tier: tier,
        billing_period: billingPeriod,
      });

      trackBeginCheckout({ planTier: tier, billingPeriod });
      window.location.href = response.data.checkout_url;

    } catch (error) {
      console.error('Error creating checkout:', error);
      const detail = error.response?.data?.detail;
      if (error.response?.status === 400 && typeof detail === 'string' && detail.toLowerCase().includes('onboarding')) {
        window.location.href = '/business/onboarding';
        return;
      }
      if (error.response?.status === 401) {
        toast.error('Please sign in to subscribe.');
      } else {
        toast.error(errorMessage(error, 'Failed to start checkout. Please try again.'));
      }
      setCheckoutLoading(null);
    }
  }, [billingPeriod]);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await businessApi.get('/subscription-tiers');
        const tiers = response.data.tiers;

        const plansObj = {};
        tiers.forEach(tier => {
          plansObj[tier.tier_key] = {
            name: tier.name,
            description: tier.description,
            monthly_price: tier.monthly_price,
            annual_price: tier.annual_price,
            deal_limit: tier.deal_limit,
            features: tier.features,
            badge: tier.badge_text,
            color_theme: tier.color_theme
          };
        });

        setPlans(plansObj);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching plans:', error);
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  // Auto-trigger checkout when user becomes authenticated after clicking a plan
  useEffect(() => {
    if (isAuthenticated && pendingPlanTier && !hasTriggeredCheckout.current) {
      hasTriggeredCheckout.current = true;
      setTimeout(() => {
        initiateCheckout(pendingPlanTier);
        setPendingPlanTier(null);
        hasTriggeredCheckout.current = false;
      }, 500);
    }
  }, [isAuthenticated, pendingPlanTier, initiateCheckout]);

  const handleSelectPlan = async (tier) => {
    if (!isAuthenticated) {
      setPendingPlanTier(tier);
      openAuthModal('signup');
      return;
    }

    await initiateCheckout(tier);
  };

  // V5 Loading State - Purple spinner on Cream background
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FEF7ED' }}>
        <Loader2 className="animate-spin" size={48} style={{ color: '#8b5cf6' }} />
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen py-16 px-4" style={{ backgroundColor: '#FEF7ED' }}>
        {/* V5 Header */}
        <div className="max-w-6xl mx-auto text-center mb-12">
          {/* V5 Code Accent */}
          <p className="font-mono text-xs mb-2" style={{ color: '#8b5cf6' }}>
            // subscription_plans
          </p>
          
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-[32px] md:text-[40px] mb-4"
            style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500, color: '#1e1b4b' }}
          >
            Choose Your Plan
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-[16px]"
            style={{ fontFamily: "'DM Sans', sans-serif", color: 'rgba(30, 27, 75, 0.7)' }}
          >
            Monthly or annual — annual is two months free. Change plan or cancel anytime in the billing portal.
          </motion.p>

          {/* V5 Billing Toggle - Orange for billing per design rules */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 mt-8 p-1.5 bg-white"
            style={{ 
              borderRadius: '9999px',
              boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)'
            }}
          >
            <button
              onClick={() => setBillingPeriod('monthly')}
              className="px-6 py-2.5 font-semibold text-[14px] transition-all duration-200"
              style={billingPeriod === 'monthly' 
                ? { 
                    background: 'linear-gradient(135deg, #f97316, #ea580c)', 
                    color: 'white', 
                    borderRadius: '9999px',
                    boxShadow: '0 4px 15px rgba(249, 115, 22, 0.4)'
                  }
                : { 
                    color: 'rgba(30, 27, 75, 0.5)',
                    borderRadius: '9999px'
                  }
              }
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingPeriod('annual')}
              className="px-6 py-2.5 font-semibold text-[14px] transition-all duration-200 flex items-center gap-2"
              style={billingPeriod === 'annual' 
                ? { 
                    background: 'linear-gradient(135deg, #f97316, #ea580c)', 
                    color: 'white', 
                    borderRadius: '9999px',
                    boxShadow: '0 4px 15px rgba(249, 115, 22, 0.4)'
                  }
                : { 
                    color: 'rgba(30, 27, 75, 0.5)',
                    borderRadius: '9999px'
                  }
              }
            >
              Annual
              <span 
                className="text-[11px] px-2 py-0.5 font-bold"
                style={{ 
                  backgroundColor: '#22c55e', 
                  color: 'white',
                  borderRadius: '9999px'
                }}
              >
                2 months free
              </span>
            </button>
          </motion.div>
        </div>

        {/* V5 Plans Grid */}
        {canceled && (
          <div className="max-w-3xl mx-auto mb-8 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-[14px] text-[#1e1b4b]">
            Checkout was cancelled — nothing was charged. Pick a plan whenever you're ready.
          </div>
        )}
        <div className="max-w-3xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
          {Object.entries(plans).map(([tier, plan], index) => {
            const Icon = TIER_ICONS[tier] || MapPin;
            const color = TIER_COLORS[tier] || THEME_COLORS[plan.color_theme] || '#8b5cf6';
            const isPopular = !!plan.badge;
            const price = billingPeriod === 'monthly' ? plan.monthly_price : plan.annual_price;
            const pricePerMonth = billingPeriod === 'annual' ? (Number(price || 0) / 12) : price;
            const features = plan.features || [];

            return (
              <motion.div
                key={tier}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="relative bg-white p-6"
                style={{ 
                  borderRadius: '12px',
                  border: isPopular ? `2px solid ${color}` : '1px solid rgba(30, 27, 75, 0.05)',
                  borderLeft: `4px solid ${color}`,
                  boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)',
                  transform: isPopular ? 'scale(1.02)' : 'scale(1)'
                }}
              >
                {/* V5 Badge */}
                {plan.badge && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span 
                      className="px-4 py-1 text-[12px] font-bold text-white"
                      style={{
                        borderRadius: '9999px',
                        background: `linear-gradient(135deg, ${color}, ${color}dd)`,
                        boxShadow: `0 4px 12px ${color}66`
                      }}
                    >
                      {plan.badge}
                    </span>
                  </div>
                )}

                {/* V5 Icon Container */}
                <div
                  className="w-12 h-12 flex items-center justify-center mb-4"
                  style={{ 
                    backgroundColor: `${color}15`,
                    borderRadius: '12px'
                  }}
                >
                  <Icon size={24} style={{ color }} />
                </div>

                {/* Plan Name - V5 Typography */}
                <h3
                  className="text-[20px] font-bold mb-2"
                  style={{ color }}
                >
                  {plan.name}
                </h3>
                
                {/* Description */}
                {plan.description && (
                  <p className="text-[13px] mb-4" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
                    {plan.description}
                  </p>
                )}

                {/* Price - V5 Typography */}
                <div className="mb-6">
                  <div className="flex items-baseline">
                    <span className="text-[36px] font-bold" style={{ color: '#1e1b4b' }}>
                      ${fmt(pricePerMonth)}
                    </span>
                    <span className="text-[16px] ml-1" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>/mo</span>
                  </div>
                  {billingPeriod === 'annual' && (
                    <p className="text-[13px] mt-1" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
                      ${fmt(price)} billed annually
                    </p>
                  )}
                </div>

                {/* Features - V5 Style */}
                <ul className="space-y-3 mb-6">
                  {features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-[14px]" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                      <Check size={16} className="flex-shrink-0 mt-0.5" style={{ color }} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* V5 CTA Button */}
                <button
                  onClick={() => handleSelectPlan(tier)}
                  disabled={checkoutLoading === tier}
                  className="w-full py-3 text-[14px] font-bold text-white transition-all duration-200 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  style={{
                    background: checkoutLoading === tier
                      ? 'rgba(30, 27, 75, 0.3)'
                      : `linear-gradient(135deg, ${color}, ${color}dd)`,
                    borderRadius: '12px',
                    boxShadow: checkoutLoading === tier 
                      ? 'none' 
                      : `0 4px 15px ${color}66`
                  }}
                >
                  {checkoutLoading === tier ? (
                    <>
                      <Loader2 className="animate-spin" size={16} />
                      Processing...
                    </>
                  ) : (
                    (preselected === tier ? `Continue with ${plan.name}` : `Choose ${plan.name}`)
                  )}
                </button>
              </motion.div>
            );
          })}
        </div>

        {/* V5 Footer Note */}
        <div className="max-w-4xl mx-auto mt-16 text-center">
          <p className="text-[14px]" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
            Billed securely by Stripe. Change plan, update your card or cancel any time from the billing portal in your dashboard.
          </p>
        </div>
      </div>

    </>
  );
};

export default SubscriptionPlans;
